import { createContext } from '$utils/index';

type MenuTheme = 'dark' | 'light' | 'default';
type LayoutContext = {
  menuTheme: MenuTheme;
  setMenuTheme: (nextMenuTheme: MenuTheme) => void;

  overlayOpen: boolean;
  setOverlayOpen: (fnOverlayOpen: (overlayOpen: boolean) => boolean) => void;

  opened: boolean;
  setHasOpened: (fnOpened: (opened: boolean) => boolean) => void;

  navbarOpen: boolean;
  setNavbarOpen: (fnNavbarOpen: (navbarOpen: boolean) => boolean) => void;

  transition: boolean;
  setTransition: (fnTransition: (transition: boolean) => boolean) => void;

  backDropActive: boolean;
  setBackDropActive: (
    fnBackDropActive: (backDropActive: boolean) => boolean,
  ) => void;

  overLayTitle: string;
  setOverLayTitle: (fnOverLayTitle: (overLayTitle: string) => string) => void;

  overLayAsset: string;
  setOverLayAsset: (fnOverLayAsset: (overLayAsset: string) => string) => void;

  overlayCtaS: any;
  setOverlayCtaS: (fnOverlayCtaS: (overlayCtaS: any) => any) => void;

  overLayCopy: JSX.Element;
  setOverLayCopy: (
    fnOverLayCopy: (overLayCopy: JSX.Element) => JSX.Element,
  ) => void;

  overlayContentOpen: boolean;
  setOverlayContentOpen: (
    fnOverlayContentOpen: (overlayContentOpen: boolean) => boolean,
  ) => void;

  startPosX: number;
  setStartPosX: (fnStartPosX: (startPosX: number) => number) => void;

  windowScroll: number;
  setWindowScroll: (fnWindowScroll: (windowScroll: number) => number) => void;

  startPosY: number;
  setStartPosY: (fnStartPosY: (startPosY: number) => number) => void;

  resized: string;
  setResized: (fnResized: (resized: string) => string) => void;

  resizedWidth: string;
  setResizedWidth: (fnResizedWidth: (resizedWidth: string) => string) => void;

  callingButton: string;
  setCallingButton: (
    fnCallingButton: (callingButton: string) => string,
  ) => void;
};

const store = (set): LayoutContext => ({
  menuTheme: 'default',
  setMenuTheme: (nextMenuTheme) => {
    set((state) => ({
      ...state,
      menuTheme: nextMenuTheme,
    }));
  },

  overlayOpen: false,
  setOverlayOpen: (fnOverlayOpen) => {
    set((state) => ({
      ...state,
      overlayOpen: fnOverlayOpen(state.overlayOpen),
    }));
  },

  navbarOpen: false,
  setNavbarOpen: (fnNavbarOpen) => {
    set((state) => ({
      ...state,
      navbarOpen: fnNavbarOpen(state.navbarOpen),
    }));
  },

  opened: false,
  setHasOpened: (fnOpened) => {
    set((state) => ({
      ...state,
      opened: fnOpened(state.opened),
    }));
  },

  transition: false,
  setTransition: (fnTransition) => {
    set((state) => ({
      ...state,
      transition: fnTransition(state.transition),
    }));
  },

  backDropActive: false,
  setBackDropActive: (fnBackDropActive) => {
    set((state) => ({
      ...state,
      backDropActive: fnBackDropActive(state.backDropActive),
    }));
  },

  overLayTitle: '',
  setOverLayTitle: (fnOverLayTitle) => {
    set((state) => ({
      ...state,
      overLayTitle: fnOverLayTitle(state.overLayTitle),
    }));
  },

  overLayAsset: '',
  setOverLayAsset: (fnOverLayAsset) => {
    set((state) => ({
      ...state,
      overLayAsset: fnOverLayAsset(state.overLayAsset),
    }));
  },

  overLayCopy: null,
  setOverLayCopy: (fnOverLayCopy) => {
    set((state) => ({
      ...state,
      overLayCopy: fnOverLayCopy(state.overLayCopy),
    }));
  },

  resized: '100dvh',
  setResized: (fnResized) => {
    set((state) => ({
      ...state,
      resized: fnResized(state.resized),
    }));
  },

  resizedWidth: '100vw',
  setResizedWidth: (fnResizedWidth) => {
    set((state) => ({
      ...state,
      resizedWidth: fnResizedWidth(state.resizedWidth),
    }));
  },

  overlayCtaS: null,
  setOverlayCtaS: (fnOverlayCtaS) => {
    set((state) => ({
      ...state,
      overlayCtaS: fnOverlayCtaS(state.overlayCtaS),
    }));
  },

  overlayContentOpen: false,
  setOverlayContentOpen: (fnOverlayContentOpen) => {
    set((state) => ({
      ...state,
      overlayContentOpen: fnOverlayContentOpen(state.overlayContentOpen),
    }));
  },

  startPosX: 0,
  setStartPosX: (fnStartPosX) => {
    set((state) => ({
      ...state,
      startPosX: fnStartPosX(state.startPosX),
    }));
  },

  windowScroll: 0,
  setWindowScroll: (fnWindowScroll) => {
    set((state) => ({
      ...state,
      windowScroll: fnWindowScroll(state.windowScroll),
    }));
  },

  startPosY: 0,
  setStartPosY: (fnStartPosY) => {
    set((state) => ({
      ...state,
      startPosY: fnStartPosY(state.startPosY),
    }));
  },

  callingButton: 'menu',
  setCallingButton: (fnCallingButton) => {
    set((state) => ({
      ...state,
      callingButton: fnCallingButton(state.callingButton),
    }));
  },
});

const [LayoutContext, LayoutProvider] = createContext<LayoutContext>(store);

export { LayoutContext, LayoutProvider };
